<script setup lang="ts">
import { ref, computed, watch } from "vue";
import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import { notificationsApi } from "@/services/notifications";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import PageHeader from "@/components/PageHeader.vue";
import {
  NotificationType,
  NotificationSeverity,
  NotificationCategory,
  type IOrg,
} from "@/types";

const form = ref({
  title: "",
  message: "",
  type: NotificationType.System,
  severity: NotificationSeverity.Info,
  actionUrl: "",
  actionText: "",
  targetType: "all" as "all" | "org" | "user",
  targetOrgId: "",
});

const sending = ref(false);
const successMessage = ref("");
const errorMessage = ref("");
const selectedOrganization = ref<IOrg | null>(null);
const availableOrganizations = ref<IOrg[]>([]);
const isLoadingOrgs = ref(false);
const hasOrgError = ref(false);
const orgErrorType = ref<'connection' | 'http' | 'general'>('general');

// User picker (Specific User target). The admin first picks an org, then the user list cascades
// from GET /api/orgs/{orgId}/users — SuperAdmin passes the CanManageUsers gate, so any org id
// works without a dedicated endpoint (same approach as AdminActivityView's actor filter).
// Raw API rows are plain JSON, not the IUser class (no getInitials()), hence a local shape.
interface OrgUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}
const userOrganization = ref<IOrg | null>(null);
const selectedUser = ref<OrgUser | null>(null);
const availableUsers = ref<OrgUser[]>([]);
const isLoadingUsers = ref(false);

function userLabel(user: OrgUser): string {
  const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return fullName || user.email || user.id;
}

const notificationTypes = computed(() => {
  return Object.keys(NotificationType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      value: NotificationType[key as keyof typeof NotificationType],
      label: key,
    }));
});

const notificationSeverities = computed(() => {
  return Object.keys(NotificationSeverity)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      value: NotificationSeverity[key as keyof typeof NotificationSeverity],
      label: key,
    }));
});

const canSend = computed(() => {
  return (
    form.value.title.trim() &&
    form.value.message.trim() &&
    (form.value.targetType === "all" ||
      (form.value.targetType === "org" && selectedOrganization.value !== null) ||
      (form.value.targetType === "user" && selectedUser.value !== null))
  );
});

async function sendNotification() {
  if (!canSend.value) {
    errorMessage.value = "Please fill in all required fields";
    return;
  }

  // Clear previous messages
  successMessage.value = "";
  errorMessage.value = "";
  sending.value = true;

  try {
    const response = await notificationsApi.broadcast({
      type: form.value.type,
      title: form.value.title,
      message: form.value.message,
      severity: form.value.severity,
      actionUrl: form.value.actionUrl || undefined,
      actionText: form.value.actionText || undefined,
      targetOrgId: form.value.targetType === "org" && selectedOrganization.value ? selectedOrganization.value.orgId : undefined,
      targetUserId: form.value.targetType === "user" && selectedUser.value ? selectedUser.value.id : undefined,
    });

    // Reset form first so the preview disappears
    resetForm();

    // Show inline success message
    successMessage.value = `Successfully sent ${response.notificationCount} notification${response.notificationCount !== 1 ? 's' : ''}`;

    // Auto-dismiss success message after 5 seconds
    setTimeout(() => {
      successMessage.value = "";
    }, 5000);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "An error occurred while sending notifications";
  } finally {
    sending.value = false;
  }
}

function dismissSuccess() {
  successMessage.value = "";
}

function dismissError() {
  errorMessage.value = "";
}

function resetForm() {
  form.value = {
    title: "",
    message: "",
    type: NotificationType.System,
    severity: NotificationSeverity.Info,
    actionUrl: "",
    actionText: "",
    targetType: "all",
    targetOrgId: "",
  };
  selectedOrganization.value = null;
  userOrganization.value = null;
  selectedUser.value = null;
  availableUsers.value = [];
}

async function loadOrganizations() {
  isLoadingOrgs.value = true;
  hasOrgError.value = false;
  errorMessage.value = "";
  try {
    const baseUrl = `${import.meta.env.VITE_API_URL}/orgs`;
    const allOrgs = (await fetchWrapper.get(baseUrl)) as any[];

    availableOrganizations.value = allOrgs.map((org: any) => ({
      orgId: org.orgId || org.OrgId,
      orgName: org.orgName || org.OrgName,
      canPublishProducts: org.canPublishProducts ?? org.CanPublishProducts ?? false,
      canPartnerWithOrgs: org.canPartnerWithOrgs ?? org.CanPartnerWithOrgs ?? false,
    }));
  } catch (err) {
    console.error("Failed to load organizations:", err);
    hasOrgError.value = true;
    orgErrorType.value = 'connection'; // fetchWrapper already shows toast
    errorMessage.value = "Failed to load organizations";
  } finally {
    isLoadingOrgs.value = false;
  }
}

async function loadUsersForOrg(orgId: string) {
  isLoadingUsers.value = true;
  errorMessage.value = "";
  try {
    const url = `${import.meta.env.VITE_API_URL}/orgs/${orgId}/users`;
    availableUsers.value = await fetchWrapper.get<OrgUser[]>(url);
  } catch (err) {
    console.error("Failed to load users:", err);
    availableUsers.value = [];
    errorMessage.value = "Failed to load users for the selected organization";
  } finally {
    isLoadingUsers.value = false;
  }
}

// Cascade the user list from the org picked in the "Specific User" flow. Clearing the org (or
// the whole flow) drops the selected user and the loaded list so a stale user can't be sent.
watch(userOrganization, (org) => {
  selectedUser.value = null;
  availableUsers.value = [];
  if (org) {
    loadUsersForOrg(org.orgId);
  }
});

// Load organizations when component mounts
loadOrganizations();
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Send Notifications"
        :breadcrumbs="[{ label: 'Dashboard', href: '/' }, { label: 'Admin', href: '/admin' }, { label: 'Notifications' }]"
      />

      <!-- Success Alert -->
      <div
        v-if="successMessage"
        class="alert alert-success alert-dismissible fade show mb-3"
        role="alert"
      >
        <i class="bi bi-check-circle-fill me-2"></i>
        <strong>Success!</strong> {{ successMessage }}
        <button
          type="button"
          class="btn-close"
          @click="dismissSuccess"
          aria-label="Close"
        ></button>
      </div>

      <!-- Error State for Organizations Loading -->
      <ErrorState
        v-if="hasOrgError"
        :type="orgErrorType"
        @retry="loadOrganizations"
        title="Failed to Load Organizations"
        message="Unable to load organizations for targeting. You can still send to all users."
      />

      <!-- Error Alert -->
      <div
        v-if="errorMessage && !hasOrgError"
        class="alert alert-danger alert-dismissible fade show mb-3"
        role="alert"
      >
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        <strong>Error!</strong> {{ errorMessage }}
        <button
          type="button"
          class="btn-close"
          @click="dismissError"
          aria-label="Close"
        ></button>
      </div>

      <!-- Form Content -->
      <form @submit.prevent="sendNotification">
        <!-- Title -->
        <div class="row mb-3">
          <label for="title" class="col-sm-3 col-form-label">Title <span class="text-danger">*</span></label>
          <div class="col-sm-9">
            <input id="title" v-model="form.title" type="text" class="form-control" placeholder="Enter notification title" required />
          </div>
        </div>

        <!-- Type -->
        <div class="row mb-3">
          <label for="type" class="col-sm-3 col-form-label">Type</label>
          <div class="col-sm-9">
            <select id="type" v-model="form.type" class="form-select">
              <option v-for="type in notificationTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
            </select>
          </div>
        </div>

        <!-- Severity -->
        <div class="row mb-3">
          <label for="severity" class="col-sm-3 col-form-label">Severity</label>
          <div class="col-sm-9">
            <select id="severity" v-model="form.severity" class="form-select">
              <option v-for="severity in notificationSeverities" :key="severity.value" :value="severity.value">{{ severity.label }}</option>
            </select>
          </div>
        </div>

        <!-- Message -->
        <div class="row mb-3">
          <label for="message" class="col-sm-3 col-form-label">Message <span class="text-danger">*</span></label>
          <div class="col-sm-9">
            <textarea id="message" v-model="form.message" class="form-control" rows="3" placeholder="Enter notification message" required></textarea>
          </div>
        </div>

        <!-- Action URL -->
        <div class="row mb-3">
          <label for="actionUrl" class="col-sm-3 col-form-label">Action URL (optional)</label>
          <div class="col-sm-9">
            <input id="actionUrl" v-model="form.actionUrl" type="text" class="form-control" placeholder="/path/to/action" />
            <div class="form-text">Relative URL where users will navigate when clicking</div>
          </div>
        </div>

        <!-- Action Button Text -->
        <div class="row mb-3">
          <label for="actionText" class="col-sm-3 col-form-label">Action Button Text (optional)</label>
          <div class="col-sm-9">
            <input id="actionText" v-model="form.actionText" type="text" class="form-control" placeholder="View Details" />
          </div>
        </div>

        <!-- Target Audience -->
        <div class="row mb-3">
          <label class="col-sm-3 col-form-label">Target Audience</label>
          <div class="col-sm-9">
            <div class="d-flex gap-3">
              <div class="form-check">
                <input id="targetAll" v-model="form.targetType" class="form-check-input" type="radio" value="all" />
                <label class="form-check-label" for="targetAll">All Users</label>
              </div>
              <div class="form-check">
                <input id="targetOrgRadio" v-model="form.targetType" class="form-check-input" type="radio" value="org" />
                <label class="form-check-label" for="targetOrgRadio">Specific Organization</label>
              </div>
              <div class="form-check">
                <input id="targetUser" v-model="form.targetType" class="form-check-input" type="radio" value="user" />
                <label class="form-check-label" for="targetUser">Specific User</label>
              </div>
            </div>
          </div>
        </div>

        <!-- Organization Selection (conditional) -->
        <div v-if="form.targetType === 'org'" class="row mb-3">
          <label for="targetOrgSelect" class="col-sm-3 col-form-label">Organization <span class="text-danger">*</span></label>
          <div class="col-sm-9">
            <VueMultiselect
              id="targetOrgSelect"
              v-model="selectedOrganization"
              :options="availableOrganizations"
              :custom-label="(opt: IOrg) => opt.orgName"
              :placeholder="isLoadingOrgs ? 'Loading organizations...' : 'Search and select an organization'"
              label="orgName"
              track-by="orgId"
              :loading="isLoadingOrgs"
              :searchable="true"
              :allow-empty="true"
              :disabled="sending"
              select-label=""
              deselect-label=""
            >
              <template #option="{ option }">
                <div class="d-flex align-items-center">
                  <span>{{ option.orgName }}</span>
                </div>
              </template>
              <template #singleLabel="{ option }">
                <span>{{ option.orgName }}</span>
              </template>
              <template #noResult>
                <div class="text-center py-2 text-muted">
                  <i class="bi bi-search me-1"></i>No organizations found
                </div>
              </template>
              <template #noOptions>
                <div v-if="!isLoadingOrgs" class="text-center py-2 text-muted">
                  <i class="bi bi-building me-1"></i>No organizations available
                </div>
              </template>
            </VueMultiselect>
            <div v-if="selectedOrganization" class="form-text">
              Notification will be sent to all users in {{ selectedOrganization.orgName }}
            </div>
          </div>
        </div>

        <!-- User Selection (conditional): pick an org, then a user within it -->
        <template v-if="form.targetType === 'user'">
          <!-- Step 1: Organization -->
          <div class="row mb-3">
            <label for="userOrgSelect" class="col-sm-3 col-form-label">Organization <span class="text-danger">*</span></label>
            <div class="col-sm-9">
              <VueMultiselect
                id="userOrgSelect"
                v-model="userOrganization"
                :options="availableOrganizations"
                :custom-label="(opt: IOrg) => opt.orgName"
                :placeholder="isLoadingOrgs ? 'Loading organizations...' : 'Search and select an organization'"
                label="orgName"
                track-by="orgId"
                :loading="isLoadingOrgs"
                :searchable="true"
                :allow-empty="true"
                :disabled="sending"
                select-label=""
                deselect-label=""
              >
                <template #option="{ option }">
                  <div class="d-flex align-items-center">
                    <span>{{ option.orgName }}</span>
                  </div>
                </template>
                <template #singleLabel="{ option }">
                  <span>{{ option.orgName }}</span>
                </template>
                <template #noResult>
                  <div class="text-center py-2 text-muted">
                    <i class="bi bi-search me-1"></i>No organizations found
                  </div>
                </template>
                <template #noOptions>
                  <div v-if="!isLoadingOrgs" class="text-center py-2 text-muted">
                    <i class="bi bi-building me-1"></i>No organizations available
                  </div>
                </template>
              </VueMultiselect>
              <div class="form-text">Choose the organization the user belongs to.</div>
            </div>
          </div>

          <!-- Step 2: User (cascades from the selected org) -->
          <div class="row mb-3">
            <label for="targetUserSelect" class="col-sm-3 col-form-label">User <span class="text-danger">*</span></label>
            <div class="col-sm-9">
              <VueMultiselect
                id="targetUserSelect"
                v-model="selectedUser"
                :options="availableUsers"
                :custom-label="userLabel"
                :placeholder="userOrganization
                  ? (isLoadingUsers ? 'Loading users...' : 'Search and select a user')
                  : 'Select an organization first'"
                track-by="id"
                :loading="isLoadingUsers"
                :searchable="true"
                :allow-empty="true"
                :disabled="sending || !userOrganization"
                select-label=""
                deselect-label=""
              >
                <template #option="{ option }">
                  <div class="d-flex flex-column">
                    <span>{{ userLabel(option) }}</span>
                    <small v-if="option.email && userLabel(option) !== option.email" class="text-muted">{{ option.email }}</small>
                  </div>
                </template>
                <template #singleLabel="{ option }">
                  <span>{{ userLabel(option) }}</span>
                </template>
                <template #noResult>
                  <div class="text-center py-2 text-muted">
                    <i class="bi bi-search me-1"></i>No users found
                  </div>
                </template>
                <template #noOptions>
                  <div v-if="!isLoadingUsers" class="text-center py-2 text-muted">
                    <i class="bi bi-person me-1"></i>No users in this organization
                  </div>
                </template>
              </VueMultiselect>
              <div v-if="selectedUser" class="form-text">
                Notification will be sent to {{ userLabel(selectedUser) }}
              </div>
            </div>
          </div>
        </template>

        <!-- Actions -->
        <div class="row">
          <div class="col-sm-9 offset-sm-3">
            <div class="d-flex justify-content-end gap-2">
              <button type="button" class="bb-btn-secondary-cta" @click="resetForm" :disabled="sending">Reset</button>
              <button type="submit" class="bb-btn-primary-cta" :disabled="!canSend || sending">
                <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
                {{ sending ? "Sending..." : "Send Notification" }}
              </button>
            </div>
          </div>
        </div>
      </form>

      <!-- Preview Section -->
      <div v-if="form.title || form.message" class="mt-4">
        <h2 class="mb-3">Preview</h2>
        <div class="notification-preview p-3 border rounded">
          <div class="d-flex align-items-start">
            <div class="me-3">
              <i
                :class="{
                  'bi bi-info-circle text-primary':
                    form.severity === NotificationSeverity.Info,
                  'bi bi-check-circle text-success':
                    form.severity === NotificationSeverity.Success,
                  'bi bi-exclamation-triangle text-warning':
                    form.severity === NotificationSeverity.Warning,
                  'bi bi-x-circle text-danger':
                    form.severity === NotificationSeverity.Error,
                }"
                class="fs-4"
              ></i>
            </div>
            <div class="flex-1">
              <h3 class="mb-1">{{ form.title || "Title" }}</h3>
              <p class="mb-2">{{ form.message || "Message content" }}</p>
              <button v-if="form.actionText" class="bb-btn-secondary-cta btn-sm" disabled>
                {{ form.actionText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </PanelCard>
  </PanelGrid>
</template>

<style scoped lang="scss">
.notification-preview {
  background-color: var(--bs-secondary-bg);
}

/* VueMultiselect theming is global — see App/src/scss/styles.scss (.multiselect*) */
</style>
